import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { $api } from '../../api/queryClient'
import { Input } from '../ui/input'
import { AppIcon } from '../ui/icon'
import { mdiAccountPlusOutline } from '@mdi/js'
import { useDebounce } from '../../hooks/useDebounce'

/**
 * "Attach this address to…" — a search box over the organization's parties and
 * a short result list.
 *
 * Deliberately a pick from a short list rather than a free-text field: the one
 * gesture this control exists for is a manager recognising "oui, Mme Dupont du
 * lot 12" at a glance, and confirming it writes the address back so the next
 * message resolves on its own.
 */
export function ContactPicker({ org, onChange }: { org: string; onChange: (userUid: string, name: string) => void }) {
    const { t } = useTranslation('conversations')
    const [term, setTerm] = useState('')
    const debounced = useDebounce(term, 250)

    // `q` is the name the endpoint actually filters on. It used to send
    // `search`, which the backend silently ignores, so the picker rendered the
    // default-ordered first rows whatever was typed — a latent usability bug for
    // as long as the only caller was the attach step on a thread whose contact
    // was usually near the top, and a hard failure the moment spec 2026-09-15-01
    // asked it to find one named contact in an organization with many.
    const { data, isFetching } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/users', {
        params: {
            path: { orgCode: org },
            query: { q: debounced, limit: 10 },
        },
    })

    const rows = ((data as { data?: Array<Record<string, unknown>> } | undefined)?.data ?? []).slice(0, 8)

    return (
        <div className="space-y-1" data-testid="conversation-contact-picker">
            <Input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={t('contact.attach')}
                aria-label={t('contact.attach')}
                className="h-8 text-sm"
                data-testid="conversation-contact-search"
            />
            {rows.length > 0 && (
                <ul className="border rounded-md divide-y max-h-48 overflow-y-auto">
                    {rows.map((row) => (
                        <li key={String(row.uid)}>
                            <button
                                type="button"
                                className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted/60"
                                onClick={() => onChange(String(row.uid), String(row.name ?? row.email ?? ''))}
                                data-testid={`conversation-contact-option-${String(row.uid)}`}
                            >
                                <span className="text-foreground">{String(row.name ?? row.email ?? row.uid)}</span>
                                {row.email ? <span className="text-muted-foreground"> · {String(row.email)}</span> : null}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {/* No match for a real search: the picker only ever offered a pick
                from an existing list, with no way out for a contact that
                simply doesn't exist yet (#1385). Opens in a new tab so the
                in-progress conversation/attach flow this control sits inside
                is never lost — the manager comes back and searches again. */}
            {rows.length === 0 && debounced.trim() !== '' && !isFetching && (
                <div className="border rounded-md px-2 py-1.5 text-xs space-y-1" data-testid="conversation-contact-no-match">
                    <p className="text-muted-foreground">{t('contact.no_match', { term: debounced })}</p>
                    <Link
                        to="/$org/users/create"
                        params={{ org }}
                        search={{ parent_user_uid: undefined }}
                        target="_blank"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                        data-testid="conversation-contact-create-new"
                    >
                        <AppIcon path={mdiAccountPlusOutline} size="xs" />
                        {t('contact.create_new')}
                    </Link>
                </div>
            )}
        </div>
    )
}
